import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Send, ArrowLeft, MapPin, Calendar, Tag, CreditCard, Check, Lock } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useLegalGate } from '../components/LegalAcceptanceModal';
import { API_BASE as API } from '../lib/apiBase';
import { toast } from 'sonner';


export default function BookingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { user, token } = useAuth();
  const [artist, setArtist] = useState(null);
  const [v2, setV2] = useState(false);
  const [inquireMode, setInquireMode] = useState(false);
  const [booking, setBooking] = useState(null);
  const [form, setForm] = useState({ event_type: '', date: '', location: '', message: '' });
  const [messages, setMessages] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [payMsg, setPayMsg] = useState('');
  const legalInquire = useLegalGate('create_booking_inquire');
  const legalBooking = useLegalGate('create_booking');

  const fetchBookingDetail = async (bookingId) => {
    const bRes = await fetch(`${API}/api/bookings/${bookingId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const bData = await bRes.json();
    setBooking(bData);
    setMessages(bData.messages || []);
    setContracts(bData.contracts || []);
  };

  useEffect(() => {
    fetch(`${API}/api/admin/settings/public`)
      .then((r) => r.json())
      .then((d) => { setV2(!!d.BOOKING_V2); })
      .catch(() => {});
  }, []);
  useEffect(() => {
    fetch(`${API}/api/artists/${id}`)
      .then(r => r.json())
      .then(a => { setArtist(a);
        const bp = a?.booking_profile;
        setInquireMode(!!(bp && bp.pricing_mode === 'inquire'));
        setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return;
    if (v2 && inquireMode) {
      if (!legalInquire.gate()) return;
    } else {
      if (!legalBooking.gate()) return;
    }
    setSubmitting(true);
    try {
      if (v2 && inquireMode) {
        const res = await fetch(`${API}/api/booking-offers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ artist_id: id, ...form }),
        });
        const data = await res.json();
        if (res.ok && data.id) {
          navigate(`/inquire/${data.id}`);
          setSubmitting(false);
          return;
        }
        const m = data.detail;
        const msg = typeof m === 'object' && m?.message ? m.message : (typeof m === 'string' ? m : (lang === 'es' ? 'Error' : 'Error'));
        if (res.status === 429) {
          toast.error(String(msg), { duration: 8000 });
        } else {
          toast.error(String(msg));
        }
        setSubmitting(false);
        return;
      }
      const res = await fetch(`${API}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ artist_id: id, ...form })
      });
      const data = await res.json();
      setBooking(data);
      setSignerName(user?.name || '');
      await fetchBookingDetail(data.id);
    } catch (e) {
      console.error(e);
    }
    setSubmitting(false);
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !booking) return;
    try {
      const res = await fetch(`${API}/api/bookings/${booking.id}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: newMsg })
      });
      const msg = await res.json();
      setMessages(prev => [...prev, msg]);
      setNewMsg('');
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) return (
    <div className="min-h-screen pt-14 flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!user) return (
    <div data-testid="booking-login-required" className="min-h-screen pt-14 flex flex-col items-center justify-center gap-4">
      <p className="text-pulse-muted">{lang === 'es' ? 'Inicia sesion para contratar' : 'Login to book'}</p>
      <Link to="/login" className="bg-pulse-primary text-black px-6 py-2 rounded-xl font-semibold">{t('auth.login')}</Link>
    </div>
  );

  return (
    <div data-testid="booking-page" className="min-h-screen pt-14 pb-32">
      {legalInquire.legalModal}
      {legalBooking.legalModal}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <Link to={`/artist/${id}`} className="flex items-center gap-2 text-pulse-muted hover:text-white mb-6 transition-colors duration-200">
          <ArrowLeft size={18} /> {t('common.back')}
        </Link>

        {artist && (
          <div className="flex items-center gap-4 mb-8 p-4 bg-pulse-surface border border-pulse-border rounded-xl">
            <img src={artist.photo} alt={artist.name} className="w-16 h-16 rounded-xl object-cover" />
            <div>
              <h2 className="font-heading font-bold text-lg">{artist.name}</h2>
              <p className="text-pulse-primary font-semibold">{artist.price_per_show}€ {t('artist.per_show')}</p>
            </div>
          </div>
        )}

        {!booking ? (
          /* Booking Form */
          <form onSubmit={handleSubmit} className="space-y-4">
            <h3 className="font-heading font-bold text-xl mb-2">{t('booking.title')}</h3>
            {v2 && inquireMode && (
              <p className="text-xs text-pulse-muted border border-amber-500/20 bg-amber-500/5 rounded-lg px-3 py-2 mb-4">
                {lang === 'es' ? 'Consultar disponibilidad y presupuesto de contratación' : 'Inquire about availability and booking budget'} — {lang === 'es' ? 'es un interés previo, no un contrato. 48h por fase, sin chat libre.' : 'this is a precontract interest, not a binding deal. 48h per turn; no free chat.'}
              </p>
            )}
            <div>
              <label className="text-xs text-pulse-muted mb-1 flex items-center gap-1">
                <Tag size={12} /> {t('booking.event_type')}
              </label>
              <select
                data-testid="booking-event-type"
                value={form.event_type}
                onChange={(e) => setForm(f => ({ ...f, event_type: e.target.value }))}
                className="w-full bg-pulse-surface border border-pulse-border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                required
              >
                <option value="">{lang === 'es' ? 'Seleccionar...' : 'Select...'}</option>
                <option value="fiesta_privada">{lang === 'es' ? 'Fiesta Privada' : 'Private Party'}</option>
                <option value="evento_corporativo">{lang === 'es' ? 'Evento Corporativo' : 'Corporate Event'}</option>
                <option value="concierto">{lang === 'es' ? 'Concierto' : 'Concert'}</option>
                <option value="festival">{lang === 'es' ? 'Festival' : 'Festival'}</option>
                <option value="boda">{lang === 'es' ? 'Boda' : 'Wedding'}</option>
                <option value="otro">{lang === 'es' ? 'Otro' : 'Other'}</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-pulse-muted mb-1 flex items-center gap-1">
                <Calendar size={12} /> {t('booking.date')}
              </label>
              <input
                data-testid="booking-date"
                type="date"
                value={form.date}
                onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full bg-pulse-surface border border-pulse-border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="text-xs text-pulse-muted mb-1 flex items-center gap-1">
                <MapPin size={12} /> {t('booking.location')}
              </label>
              <input
                data-testid="booking-location"
                type="text"
                value={form.location}
                onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))}
                placeholder={lang === 'es' ? 'Ciudad y lugar del evento' : 'City and venue'}
                className="w-full bg-pulse-surface border border-pulse-border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="text-xs text-pulse-muted mb-1">{t('booking.message')}</label>
              <textarea
                data-testid="booking-message"
                value={form.message}
                onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                placeholder={lang === 'es' ? 'Detalles adicionales...' : 'Additional details...'}
                rows={3}
                className="w-full bg-pulse-surface border border-pulse-border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none resize-none"
              />
            </div>

            <button
              data-testid="submit-booking"
              type="submit"
              disabled={submitting}
              className="w-full bg-pulse-primary text-black font-bold py-3 rounded-xl hover:bg-pulse-primary-hover transition-colors duration-200 disabled:opacity-50 drop-shadow-[0_0_12px_rgba(0,212,255,0.4)]"
            >
              {submitting ? (lang === 'es' ? 'Enviando...' : 'Sending...') : t('booking.send')}
            </button>
          </form>
        ) : (
          /* Chat */
          <div data-testid="booking-chat">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-pulse-primary flex items-center justify-center">
                <span className="text-black font-bold text-xs">P</span>
              </div>
              <span className="font-heading font-bold">PulseMusic</span>
              <span className="text-xs text-pulse-muted">{lang === 'es' ? 'Equipo de gestión' : 'Operations team'}</span>
            </div>

            <div className="space-y-3 max-h-[50vh] overflow-y-auto mb-4 p-2">
              {messages.map(msg => (
                <div
                  key={msg.id}
                  className={`max-w-[80%] ${msg.sender === 'user' ? 'ml-auto' : ''}`}
                >
                  <div className={`p-3 rounded-xl text-sm ${
                    msg.sender === 'user'
                      ? 'bg-pulse-primary text-black'
                      : 'bg-pulse-surface border border-pulse-border'
                  }`}>
                    {msg.message}
                  </div>
                  <p className="text-[10px] text-pulse-muted mt-1">
                    {msg.sender_name || (msg.sender === 'pluka' || msg.sender === 'admin' ? 'PulseMusic' : t('nav.profile'))}
                  </p>
                </div>
              ))}
            </div>

            {/* Booking details */}
            <div className="p-4 bg-pulse-surface border border-pulse-border rounded-xl mb-4 space-y-2 text-sm">
              <p><span className="text-pulse-muted">{t('booking.date')}:</span> {booking.date}</p>
              <p><span className="text-pulse-muted">{t('booking.location')}:</span> {booking.location}</p>
              <p><span className="text-pulse-muted">{t('booking.event_type')}:</span> {booking.event_type}</p>
              <p><span className="text-pulse-muted">{t('booking.status')}:</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  booking.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                  booking.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                  'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {t(`booking.${booking.status}`)}
                </span>
              </p>
              <p><span className="text-pulse-muted">{lang === 'es' ? 'Precio' : 'Price'}:</span> <span className="font-bold text-pulse-primary">{booking.price}€</span></p>
              
              {booking.paid && (
                <div className="mt-2 p-2 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-2 text-xs text-green-400">
                  <Check size={14} /> {lang === 'es' ? 'Pagado. Fondos retenidos hasta confirmacion del evento.' : 'Paid. Funds held until event confirmation.'}
                </div>
              )}
              {booking.escrow_released && (
                <div className="mt-2 p-2 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-2 text-xs text-green-400">
                  <Check size={14} /> {lang === 'es' ? 'Evento confirmado. Fondos liberados al artista.' : 'Event confirmed. Funds released to artist.'}
                </div>
              )}

              {(contracts || []).length > 0 && (
                <div data-testid="booking-contracts-panel" className="mt-3 rounded-2xl border border-pulse-border bg-pulse-surface p-3 space-y-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-pulse-muted">{lang === 'es' ? 'Contrato asignado' : 'Assigned contract'}</p>
                    <p className="text-sm font-semibold">{lang === 'es' ? 'Firma digital antes del pago' : 'Digital signature before payment'}</p>
                  </div>
                  {contracts.map(contract => {
                    const clientSigned = (contract.signatures || []).some(sig => sig.signer_role === 'client');
                    return (
                      <div key={contract.id} className="rounded-xl border border-pulse-border bg-pulse-elevated p-3 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold">{contract.title_es || contract.title_en}</p>
                            <p className="text-xs text-pulse-muted mt-1">{contract.status} · {(contract.required_signers || []).join(', ')}</p>
                          </div>
                          {clientSigned && <span className="text-[11px] text-green-400">{lang === 'es' ? 'Firmado por ti' : 'Signed by you'}</span>}
                        </div>
                        <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                          <pre className="whitespace-pre-wrap text-xs leading-relaxed text-cyan-50">{contract.rendered_es || contract.rendered_en}</pre>
                        </div>
                        {!clientSigned && (
                          <div className="space-y-2">
                            <input data-testid={`contract-signer-name-${contract.id}`} value={signerName} onChange={e => setSignerName(e.target.value)} placeholder={lang === 'es' ? 'Tu nombre legal' : 'Your legal name'} className="w-full bg-pulse-surface border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                            <button
                              data-testid={`contract-sign-button-${contract.id}`}
                              onClick={async () => {
                                setSigning(true);
                                const res = await fetch(`${API}/api/contracts/instances/${contract.id}/sign`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                  body: JSON.stringify({ signer_name: signerName })
                                });
                                const data = await res.json();
                                if (res.ok) {
                                  setPayMsg(lang === 'es' ? 'Contrato firmado correctamente.' : 'Contract signed successfully.');
                                  setContracts(prev => prev.map(item => item.id === contract.id ? data : item));
                                  await fetchBookingDetail(booking.id);
                                } else {
                                  setPayMsg(data.detail || 'Error');
                                }
                                setSigning(false);
                              }}
                              disabled={signing || !signerName.trim()}
                              className="w-full bg-pulse-primary text-black font-bold py-2.5 rounded-xl disabled:opacity-50"
                            >
                              {lang === 'es' ? 'Firmar digitalmente' : 'Sign digitally'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {booking.status === 'confirmed' && !booking.paid && (
                <div className="mt-3">
                  <button
                    data-testid="pay-booking-btn"
                    onClick={async () => {
                      setPaying(true);
                      try {
                        const res = await fetch(`${API}/api/bookings/${booking.id}/pay`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
                        });
                        const data = await res.json();
                        if (res.ok) {
                          setPayMsg(lang === 'es' ? `Pagado. ${data.artist_share}€ para el artista (retenido), ${data.pluka_share}€ para gestión y ${data.admin_share}€ para plataforma.` : `Paid. ${data.artist_share}€ for the artist (held), ${data.pluka_share}€ for operations and ${data.admin_share}€ for platform.`);
                          await fetchBookingDetail(booking.id);
                        } else {
                          setPayMsg(data.detail || 'Error');
                        }
                      } catch (e) { setPayMsg('Error'); }
                      setPaying(false);
                    }}
                    disabled={paying}
                    className="w-full bg-green-500 text-black font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-green-400 transition-colors disabled:opacity-50"
                  >
                    <CreditCard size={16} /> {paying ? '...' : (lang === 'es' ? `Pagar ${booking.price}€ (desde monedero)` : `Pay ${booking.price}€ (from wallet)`)}
                  </button>
                  {payMsg && <p className="text-xs mt-2 text-center text-pulse-muted">{payMsg}</p>}
                  <p className="text-[10px] text-pulse-muted mt-1 text-center flex items-center justify-center gap-1">
                    <Lock size={8} /> {lang === 'es' ? '70% retenido hasta que el equipo confirme el evento. 30% para plataforma y gestión.' : '70% held until the team confirms the event. 30% for platform and operations.'}
                  </p>
                </div>
              )}
            </div>

            {/* Message input */}
            <div className="flex gap-2">
              <input
                data-testid="chat-input"
                type="text"
                value={newMsg}
                onChange={(e) => setNewMsg(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder={lang === 'es' ? 'Escribe un mensaje...' : 'Type a message...'}
                className="flex-1 bg-pulse-surface border border-pulse-border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
              />
              <button
                data-testid="send-chat-msg"
                onClick={sendMessage}
                className="bg-pulse-primary text-black p-3 rounded-xl hover:bg-pulse-primary-hover transition-colors duration-200"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
